'use client';

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';

/** Shared contained scrollport, with edge shadows only where more columns remain. */
export function TableScrollRegion({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () => {
      const left = element.scrollLeft > 1;
      const right = element.scrollWidth - element.clientWidth - element.scrollLeft > 1;
      setEdges((previous) =>
        previous.left === left && previous.right === right ? previous : { left, right },
      );
    };
    update();
    element.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(element);
    if (element.firstElementChild) observer?.observe(element.firstElementChild);
    return () => {
      element.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
  }, [children]);
  return (
    <div className="relative w-full min-w-0 max-w-full" data-table-scroll>
      <div
        ref={ref}
        data-scroll-right={edges.right}
        role={edges.left || edges.right ? 'region' : undefined}
        aria-label={edges.left || edges.right ? 'Scrollable table' : undefined}
        tabIndex={edges.left || edges.right ? 0 : undefined}
        className={cn(
          'w-full min-w-0 max-w-full overflow-x-auto [&[data-scroll-right=true]_[data-pinned-actions]]:border-l [&[data-scroll-right=true]_[data-pinned-actions]]:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-inset',
          className,
        )}
      >
        {children}
      </div>
      {edges.left && (
        <div
          data-scroll-edge="left"
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 z-30 w-3 bg-gradient-to-r from-border to-transparent"
        />
      )}
      {edges.right && (
        <div
          data-scroll-edge="right"
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 z-30 w-3 bg-gradient-to-l from-border to-transparent"
        />
      )}
    </div>
  );
}
